import {BlHostError, ErrorCode} from "./BlHostError";
import struct from "python-struct";

const debug = require('debug')('dfu:transport');
const events = require('events');

/**
 * Implements the common BlHost logic to all transports, but not the transport itself.
 *
 * It says "Abstract" in the class name, so do not instantiate directly.
 * You should Subclass, and add/alter the functionality of the needed methods
 * with the actual transport logic.
 */
export default class DfuAbstractTransport {
    constructor() {
        if (this.constructor === DfuAbstractTransport) {
            // throw new BlHostError(ErrorCode.ERROR_CAN_NOT_INIT_ABSTRACT_TRANSPORT);
            throw Error("Cannot init abstract transport class");
        }
        this.events = new events.EventEmitter();

        // Store *one* message waiting to be read()
        this.lastReceivedPacket = undefined;

        // Store *one* reference to a read() callback function
        this.waitingForPacket = undefined;
    }

    // Requests a response to a previous command or data input.
    // Returns a Promise to [statusCode, Uint8Array].
    // Can only have one pending request at any time
    read() {
        if (this.waitingForPacket) {
            throw new BlHostError(ErrorCode.ERROR_READ_CONFLICT);
        }

        if (this.lastReceivedPacket) {
            const packet = this.lastReceivedPacket;
            delete this.lastReceivedPacket;
            return Promise.resolve(packet);
        }

        // Store the callback, so it can be called as soon as the wire packet is ready.
        // Add a 5sec timeout while we're at it; remove the timeout when data is actually received.
        return new Promise((res, rej) => {
            let timeout;

            const readCallback = (data => {
                clearTimeout(timeout);
                res(data);
            });

            timeout = setTimeout(() => {
                if (this.waitingForPacket && this.waitingForPacket === readCallback) {
                    delete this.waitingForPacket;
                }
                rej(new BlHostError(ErrorCode.ERROR_TIMEOUT_READING_SERIAL));
            }, 5000);

            this.waitingForPacket = readCallback;
        });
    }

    // Must be called when a (complete) packet/message is received, with the
    // (decoded) bytes of the entire packet/message. Either stores the packet
    // just received, or calls the pending read() callback to unlock it
    onData(bytes) {
        if (this.lastReceivedPacket) {
            throw new BlHostError(ErrorCode.ERROR_RECEIVE_TWO_MESSAGES);
        }

        if (this.waitingForPacket) {
            const callback = this.waitingForPacket;
            delete this.waitingForPacket;
            return callback(this.parse(bytes));
        }

        this.lastReceivedPacket = this.parse(bytes);
        return undefined;
    }

    // Parses a received BlHost response packet/message, does a couple of checks,
    // then returns an array in the form of [commandTag, responseParameters], if the operation was successful.
    // If there were any errors, returns a rejected Promise with an error message.
    parse(data) { // eslint-disable-line class-methods-use-this
        // Basic packet validation
        const commandTag = data[0];
        const flags = data[1];
        const reserved = data[2];
        const paramCount = data[3];

        if (reserved) {
            return Promise.reject(new BlHostError(ErrorCode.ERROR_UNEXPECTED_RESPONSE_BYTES));
        }

        debug("warning: flags == 1: data packets are to follow this packet");

        // Check response length
        if ((paramCount * 4) != (data.length - 4)) {
            return Promise.reject(new BlHostError(ErrorCode.ERROR_UNEXPECTED_RESPONSE_BYTES));
        }

        // Extract responseParameters
        const responseParameters = data.subarray(4);

        return Promise.resolve([commandTag, responseParameters]);
    }


    sendPayloadChunk(type, bytes, start, end, chunkSize) {
        const subarray = bytes.subarray(start, end);
        // const crcAtChunkEnd = crc32(subarray, crcSoFar);

        return this.writeObject(subarray, start)
            .then(() => {
                debug('Payload type fully transferred, requesting explicit checksum');
                // return this.crcObject(end, crcAtChunkEnd);
                return;
            })
            .then(([offset, crc]) => {
                if (offset !== end) {
                    throw new BlHostError(ErrorCode.ERROR_UNEXPECTED_BYTES, `Expected ${end} bytes to have been sent, actual is ${offset} bytes.`);
                }

                if (crcAtChunkEnd !== crc) {
                    throw new BlHostError(ErrorCode.ERROR_CRC_MISMATCH, `CRC mismatch after ${end} bytes have been sent: expected ${crcAtChunkEnd}, got ${crc}.`);
                } else {
                    debug(`Explicit checksum OK at ${end} bytes`);
                }
            })
            .catch(err => {
                if (retries >= 5) {
                    return Promise.reject(new BlHostError(ErrorCode.ERROR_TOO_MANY_WRITE_FAILURES, `Last failure: ${err}`));
                }
                debug(`Chunk write failed (${err}) Re-sending the whole chunk starting at ${start}. Times retried: ${retries}`);

                // FIXME: Instead of re-creating the whole chunk, select the payload
                // type again and check the CRC so far.

                const newStart = start - (start % chunkSize);
                // Rewind to the start of the block
                // const rewoundCrc = newStart === 0 ? undefined : crc32(bytes.subarray(0, newStart));

                return this.createObject(type, end - start)
                    .then(() => this.sendPayloadChunk(
                        type, bytes, newStart, end,
                        chunkSize, rewoundCrc, retries + 1
                    ));
            });
    }

    // Allocate space for a new payload chunk. Resets the progress
    // since the last Execute command, and selects the newly created object.
    // Must return a Promise
    // Actual implementation must be provided by concrete subclasses of DfuAbstractTransport.
    createObject(type, size) {}


    // Fill the space previously allocated with createObject() with the given bytes.
    // Also receives the absolute offset and CRC32 so far, as some wire
    // implementations perform extra CRC32 checks as the fragmented data is being
    // checksummed (and the return value for those checks involves both the absolute
    // offset and the CRC32 value). Note that the CRC and offset are not part of the
    // SDK implementation.
    // Must return a Promise to an array of [offset, crc]
    // Actual implementation must be provided by concrete subclasses of DfuAbstractTransport.
    writeObject(bytes, crcSoFar, offsetSoFar) {}
}
